/**
 * QuickJumpDock — single rounded-pill dock that holds BOTH the
 * cross-page navigation buttons (Home / Manager Dashboard / Zone
 * Sheet / Collect Emails) AND page-specific action buttons (e.g.
 * Print on Zone Sheet, Logout on Manager Dashboard). The dock is
 * the only floating header control — pages should NOT keep
 * standalone Print/Logout icons next to it.
 *
 * Responsive behavior:
 *   - Icon-only 8×8 (32px) buttons on phone (default)
 *   - 9×9 (36px) on tablet+ for easier touch
 *   - Header containers using this dock should wrap the dock to a
 *     second row on narrow viewports rather than letting it crowd
 *     the page title (see ZoneSheetPage / ManagerDashboard).
 *
 * Props:
 *   active     — one of "home" | "manager" | "zone-sheet" | "emails"
 *                Identifies the current page; renders selected
 *                styling and is rendered as a non-clickable indicator.
 *   onGoHome   — fn() called when Home is tapped.
 *   onNavigate — fn(key) called for non-Home nav buttons. Keys match
 *                App.handleHomePick: "manager", "zone-sheet", "tally"
 *                (Collect Emails).
 *   extras     — optional array of action buttons appended INSIDE the
 *                same pill, separated from the nav buttons by a thin
 *                vertical divider. Each entry shape:
 *                  { id, Icon, label, onClick, tone? }
 *                `tone` is "danger" for destructive (Logout) and
 *                renders in red on hover; otherwise neutral slate.
 */
import React from "react";
import {
  House,
  Gauge,
  ClipboardText,
  EnvelopeSimple,
} from "@phosphor-icons/react";

const NAV_DESTINATIONS = [
  { id: "home",       Icon: House,          label: "Home",              navKey: null         },
  { id: "manager",    Icon: Gauge,          label: "Manager Dashboard", navKey: "manager"    },
  { id: "zone-sheet", Icon: ClipboardText,  label: "Zone Sheet",        navKey: "zone-sheet" },
  { id: "emails",     Icon: EnvelopeSimple, label: "Collect Emails",    navKey: "tally"      },
];

const btnBase =
  "w-7 h-7 sm:w-9 sm:h-9 rounded-full inline-flex items-center justify-center " +
  "transition-colors flex-shrink-0";
const btnIdle    = "text-slate-500 hover:bg-slate-200 hover:text-slate-800 active:bg-slate-300";
const btnActive  = "bg-white shadow-sm text-blue-600 cursor-default";
const btnDanger  = "text-slate-500 hover:bg-red-100 hover:text-red-600 active:bg-red-200";

export default function QuickJumpDock({
  active,
  onGoHome,
  onNavigate,
  extras = [],
}) {
  const handleNavClick = (dest) => {
    if (dest.id === active) return;
    if (dest.id === "home") {
      onGoHome && onGoHome();
    } else {
      onNavigate && onNavigate(dest.navKey);
    }
  };

  return (
    <nav
      className="inline-flex items-center gap-0 sm:gap-0.5 bg-slate-100 rounded-full p-0.5 sm:p-1 border border-slate-200/60 flex-shrink-0"
      role="navigation"
      aria-label="Quick jump"
      data-testid="quick-jump-dock"
    >
      {NAV_DESTINATIONS.map((dest) => {
        const isActive = dest.id === active;
        const Icon = dest.Icon;
        return (
          <button
            key={dest.id}
            type="button"
            onClick={() => handleNavClick(dest)}
            disabled={isActive}
            className={`${btnBase} ${isActive ? btnActive : btnIdle}`}
            title={dest.label}
            aria-label={dest.label}
            aria-current={isActive ? "page" : undefined}
            data-testid={`quick-jump-${dest.id}`}
          >
            <Icon weight={isActive ? "fill" : "regular"} className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
          </button>
        );
      })}

      {extras.length > 0 && (
        <span
          className="mx-0.5 h-5 w-px bg-slate-300/80 flex-shrink-0"
          aria-hidden="true"
        />
      )}

      {extras.map((x) => {
        const Icon = x.Icon;
        const tone = x.tone === "danger" ? btnDanger : btnIdle;
        return (
          <button
            key={x.id}
            type="button"
            onClick={x.onClick}
            className={`${btnBase} ${tone}`}
            title={x.label}
            aria-label={x.label}
            data-testid={`quick-jump-${x.id}`}
          >
            <Icon weight="regular" className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
          </button>
        );
      })}
    </nav>
  );
}
